import { Dialog, InputV2, TextArea } from '@cimplico/components';
import { PencilSquareIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import EntityTypeSelect from '../../components/entity-type-select/EntityTypeSelect';
import { toastNotification } from '../../components/toast/Toast';
import { useUpdateCalculationTemplate } from '../../queries/resources/calculation-templates';
import { CWTBehaviourEnum } from '../../types/custom-worksheet-v2';
import { EntityTypeEnum } from '../../types/entity';
import { arrayEquals } from '../../utilities/array';
import BehaviourRadioButton from '../calculation-template/BehaviourRadioButton';
import OtherScheduleToggle from '../calculation-template/OtherScheduleToggle';

type EditFormData = {
    name: string;
    description: string;
    tags: string[];
    entityTypes: EntityTypeEnum[];
    allowAsSchedule: boolean;
    behaviour: CWTBehaviourEnum | null;
};

type EditDetailsFormProps = EditFormData & { id: string };

function EditDetailsForm(props: EditDetailsFormProps): JSX.Element {
    const [isEditing, setIsEditing] = useState(false);
    const { mutate, isLoading } = useUpdateCalculationTemplate(props.id);

    const form = useForm<EditFormData>({
        defaultValues: props,
    });

    function toggleIsEditing(): void {
        setIsEditing(!isEditing);
        form.reset(props);
    }

    function onSubmit(data: EditFormData): void {
        if (Object.keys(form.formState.dirtyFields).length === 0) {
            toggleIsEditing();
        }

        const fields: Partial<EditFormData> = {};

        Object.entries(data).forEach(([key, value]) => {
            const _key = key as keyof EditFormData;
            if (form.formState.dirtyFields[_key]) {
                // @ts-ignore
                fields[_key] = value;
            }
        });
        mutate(
            {
                name: fields.name?.trim(),
                tags: fields.tags?.map((t) => t.trim())?.filter((t) => t !== ''),
                description: fields.description?.trim(),
                entityTypes: fields.entityTypes,
                allowAsSchedule: fields.allowAsSchedule,
                behaviour: fields.behaviour,
            },
            {
                onSuccess: () => {
                    toggleIsEditing();
                    toastNotification('success', 'Successfully edited template');
                },
                onError: (err) => {
                    const formErrors: Record<string, string[]> = err.response?.data;
                    Object.entries(formErrors).forEach((entry) => {
                        const k = entry[0] as keyof EditFormData;
                        form.setError(k, { message: entry[1][0] });
                    });
                },
            }
        );
    }

    return (
        <>
            {isEditing && (
                <Dialog
                    title="Edit Details"
                    description={`Changes to these details will not affect associated worksheets until they sync the changes.`}
                    isOpen
                    primaryAction="Save"
                    secondaryAction="Cancel"
                    type="info"
                    onClose={toggleIsEditing}
                    onSecondaryActionClick={toggleIsEditing}
                    onPrimaryActionClick={form.handleSubmit(onSubmit)}
                    isLoading={isLoading}
                >
                    <div>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="mr-12 pb-4 flex flex-col gap-4">
                            {/* Name */}
                            <Controller
                                name="name"
                                control={form.control}
                                rules={{ required: 'Name is required' }}
                                render={(render): JSX.Element => (
                                    <InputV2
                                        type="text"
                                        value={render.field.value}
                                        onChange={render.field.onChange}
                                        title="Name"
                                        autoFocus
                                        errorMessage={render.fieldState.error?.message}
                                    />
                                )}
                            />
                            {/* Tag */}
                            <Controller
                                name="tags"
                                control={form.control}
                                rules={{ required: 'Tag is required' }}
                                render={(render): JSX.Element => (
                                    <InputV2
                                        type="text"
                                        value={render.field.value.join(',')}
                                        onChange={(e): void => {
                                            render.field.onChange(e.split(','));
                                        }}
                                        title="Tag"
                                        errorMessage={render.fieldState.error?.message}
                                    />
                                )}
                            />
                            <OtherScheduleToggle control={form.control} name="allowAsSchedule" />

                            <BehaviourRadioButton control={form.control} name="behaviour" />

                            {/* Entity Types */}
                            <Controller
                                name="entityTypes"
                                control={form.control}
                                render={(render): JSX.Element => {
                                    return (
                                        <EntityTypeSelect
                                            isMulti
                                            title="Entity Types"
                                            isOptional
                                            onChange={render.field.onChange}
                                            defaultEntityType={render.field.value}
                                        />
                                    );
                                }}
                            />

                            {/* Description */}
                            <Controller
                                name="description"
                                control={form.control}
                                rules={{ required: false }}
                                render={(render): JSX.Element => (
                                    <TextArea
                                        isOptional
                                        defaultValue={props.description}
                                        onChange={render.field.onChange}
                                        title="Description"
                                    />
                                )}
                            />
                            {/* This is needed to allow "enter" to trigger submit as the dialog "Save" button exists out of the form */}
                            <button type="submit" hidden />
                        </form>
                    </div>
                </Dialog>
            )}
            <button className="ml-2" onClick={toggleIsEditing}>
                <PencilSquareIcon className="text-gray-700 w-4 h-4" />
            </button>
        </>
    );
}

export default EditDetailsForm;
